<template>
  <div class="map-legend" :class="`position-${position}`">
    <!-- Color boxes -->
    <div class="legend-colors">
      <div 
        v-for="(item, index) in legendItems" 
        :key="index"
        class="color-box"
        :class="{ 'active': selectedIndex === index }"
        :style="{ backgroundColor: item.color_code }"
        :title="$t(item.text)"
        @click="toggleItem(index)"
      >
        <span v-if="showNumbers" class="color-number">{{ index + 1 }}</span>
      </div>
    </div>

    <!-- Labels panel -->
    <transition name="slide-right">
      <q-card v-if="showLabels && selectedIndex !== null" class="legend-panel" flat bordered>
        <!-- Header -->
        <!-- <div class="panel-header legend-header q-pa-md">
          <div class="col">
            <div class="text-subtitle2 text-weight-bold">
              <span v-html="layerInfo.name"></span>
              <span v-if="layerInfo.unit" class="unit-text" v-html="layerInfo.unit"></span>
            </div>
            <div v-if="layerInfo.year" class="text-caption year-text" v-html="layerInfo.year"></div>
          </div>
          <q-btn 
            @click="hideLabels" 
            icon="close" 
            flat round dense size="sm"
            :color="$q.dark.isActive ? 'grey-5' : 'grey-6'"
          />
        </div> -->
        <div class="panel-header legend-header q-pa-md">
          <div class="row items-start justify-between no-wrap full-width">
            <div class="col">
              <div class="text-subtitle2 text-weight-bold" :class="$q.screen.gt.md ? 'q-mb-xs' : ''">
                <span v-html="$t(layerInfo.name)"></span>
                <span v-if="layerInfo.unit" class="unit-text" v-html="$t(layerInfo.unit)"></span>
              </div>
              <div v-if="layerInfo.year" class="text-caption year-text" v-html="layerInfo.year"></div>
            </div>
            <q-btn 
              @click="hideLabels" 
              icon="close" 
              flat round dense size="sm"
              :color="$q.dark.isActive ? 'grey-5' : 'grey-6'"
            />
          </div>
        </div>


        <q-separator />
        
        <!-- Legend items -->
        <div class="legend-items q-pa-xs">
          <div 
            v-for="(item, index) in legendItems" 
            :key="index"
            class="legend-item"
            :class="{ 'highlighted': selectedIndex === index }"
            @click="selectItem(index)"
          >
            <div class="item-color" :style="{ backgroundColor: item.color_code }"></div>
            <span class="item-text" v-html="$t(item.text)"></span>
          </div>
        </div>

        <!-- Footer -->
        <template v-if="layerInfo.description">
          <q-separator />
          <div class="legend-footer q-pa-md">
            <div class="text-caption footer-text" v-html="$t(layerInfo.description)"></div>
          </div>
        </template>
      </q-card>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'MapLegend',
  
  props: {
    legendData: { type: Array, default: () => [] },
    layerInfo: {
      type: Object,
      default: () => ({
        name: 'Legend',
        year: null,
        description: null,
        unit: null
      })
    },
    showNumbers: { type: Boolean, default: false },
    position: {
      type: String,
      default: 'center',
      validator: v => ['top', 'center', 'bottom'].includes(v)
    },
    activePanel: { type: String, default: null }
  },

  emits: ['item-selected', 'legend-toggled'],

  data() {
    return {
      selectedIndex: null,
      showLabels: false
    }
  },

  computed: {
    legendItems() {
      if (!this.legendData?.length) return this.fallbackLegend
      
      return this.legendData.map((item, i) => ({
        color_code: item.color_code || item.color || `hsl(${i * 60}, 70%, 50%)`,
        text: item.text || item.label || `${this.$t('Item')} ${i + 1}`,
        value: item.value,
        from: item.from,
        to: item.to,
        active: item.active ?? true
      }))
    },

    fallbackLegend() {
      return [
        { color_code: '#0000ff', text: this.$t('Low') + ' (0-20%)' },
        { color_code: '#00ffff', text: this.$t('Medium-Low') + ' (20-40%)' },
        { color_code: '#ffff00', text: this.$t('Medium') + ' (40-60%)' },
        { color_code: '#ff8000', text: this.$t('Medium-High') + ' (60-80%)' },
        { color_code: '#ff0000', text: this.$t('High') + ' (80-100%)' }
      ]
    }
  },
  watch: {
      activePanel(val) {
        if (val && val !== 'legend') {
          this.hideLabels()
        }
      }
  },
  methods: {    
    toggleItem(index) {
      if (this.selectedIndex === index && this.showLabels) {
        this.hideLabels()
      } else {
        this.selectedIndex = index
        this.showLabels = true
        this.$emit('item-selected', { index, item: this.legendItems[index] })
      }
      this.$emit('legend-toggled', this.showLabels)
    },

    selectItem(index) {
      this.selectedIndex = index
      this.$emit('item-selected', { index, item: this.legendItems[index] })
    },

    hideLabels() {
      this.showLabels = false
      this.selectedIndex = null
      this.$emit('legend-toggled', false)
    }
  }
}
</script>

<style lang="scss" scoped>
.map-legend {
  position: absolute;
  right: var(--map-ui-right);
  top: 50%;
  transform: translateY(-50%);
  z-index: var(--z-map-ui);
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &.position-top {
    top: var(--map-ui-top);
    transform: none;
  }

  &.position-bottom {
    top: auto;
    bottom: var(--map-ui-bottom);
    transform: none;
  }
}

// Color boxes
.legend-colors {
  max-height: 60vh;
  overflow-y: auto;
  scrollbar-width: none;

  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  backdrop-filter: blur(5px);

  body.body--dark & {
    background: rgba($dark, 0.9);
  }
}

.color-box {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all var(--transition-fast) ease;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid transparent;

  &:hover {
    transform: scale(1.1);
    box-shadow: var(--shadow-md);
  }

  &.active {
    border-color: $grey-8;
    transform: scale(1.15);
    box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.3);

    body.body--dark & {
      border-color: white;
    }
  }
}

.color-number {
  font-size: 0.625rem;
  font-weight: bold;
  color: white;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.8);
}

// Labels panel
.legend-panel {
  min-width: 15rem;
  max-width: 22rem;
  max-height: 60vh;
  border-radius: var(--radius-md);
   overflow: hidden;
   display: flex;
  flex-direction: column;
}

.legend-header {
  border-radius: var(--radius-md) var(--radius-md) 0 0;
  background: $grey-2;

  body.body--dark & {
    background: $grey-9;
  }
}

.year-text {
  color: $grey-6;

  body.body--dark & {
    color: $grey-5;
  }
}

.unit-text {
  font-size: 0.85em;
  font-weight: normal;
  color: $grey-7;
  margin-left: 0.25rem;

  body.body--dark & {
    color: $grey-5;
  }
}

// Legend items
.legend-items {
  max-height: 18.75rem;
  overflow-y: auto;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.375rem 0.75rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all var(--transition-fast) ease;

  &:hover {
    background: $grey-2;

    body.body--dark & {
      background: $grey-9;
    }
  }

  &.highlighted {
    background: rgba($primary, 0.15);

    body.body--dark & {
      background: rgba($secondary, 0.2);
    }
  }
}

.item-color {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.1);

  body.body--dark & {
    border-color: rgba(255, 255, 255, 0.1);
  }
}

.item-text {
  flex: 1;
  font-size: 0.8125rem;
  color: $grey-8;

  body.body--dark & {
    color: $grey-4;
  }
}

// Footer
.legend-footer {
  background: $grey-2;

  body.body--dark & {
    background: $grey-9;
  }
}

.footer-text {
  color: $grey-7;

  body.body--dark & {
    color: $grey-5;
  }
}

// Animations
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all var(--transition-normal) ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(1.25rem) scale(0.95);
}

// Responsive mobile
@media (max-width: 48rem), (max-height: 48rem)  {
  .map-legend {
    right: var(--map-ui-right-mobile);
    gap: 0.5rem;

    &.position-top {
      top: var(--map-ui-top-mobile);
      transform: none;
    }

    &.position-bottom {
      bottom: var(--map-ui-bottom-mobile);
    }
  }

  .legend-colors {
    gap: 0.125rem;
    padding: 0.3rem; /* ili 0.25rem; za minimilizam */
  }
  

  .legend-panel {
    min-width: 13.75rem;
    max-width: calc(100vw - 9rem);
  }

   .color-box {
    width: 1.25rem;
    height: 1.25rem;
  }

   .legend-header {
    padding: 1rem 0.6875rem 0.5rem 0.6875rem;

    .text-subtitle2 {
      // font-size: 0.75rem;
    }

    .text-caption {
      // font-size: 0.625rem;
    }
  }

  .legend-items {
    max-height: 40vh;
  }

  .legend-item {
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
  }
  .item-color {
    width: 0.875rem;
    height: 0.875rem;
  }
   .item-text {
    font-size: 0.6875rem;
  }

  .legend-footer {
    padding: 0.375rem 0.6875rem;

    .text-caption {
      // font-size: 0.625rem;
    }
  }
}

/* landscape */
@media (max-height: 48rem) and (orientation: landscape){
  .legend-panel {
    max-width: 15.5rem;
  }

  .legend-header {
    padding: 0.6875rem 0.6875rem 0.375rem 0.6875rem;
  }
}
</style>